import { getAiInput, getAiPaceAssist } from "./ai";
import { constrainKartToOuterBand, createGridTransform, resolveKartCollisions, stepKart } from "./kartPhysics";
import { applyHiccupsInput, createItemBoxes, HICCUPS_SPEED_CAP, stepKartItems, stepProjectiles } from "./items";
import { createHazards, stepKartHazards } from "./hazards";
import { DEFAULT_TRACK_ID, getCustomTrackLayout, getTrackDefinition, getTrackLength, isClassicTrackId, sourceSampleIndexAtDistance, usesSourceIndexedCursor } from "./track";
import { buildClassicStartingGrid } from "../classicStartingGrid";
import { CUSTOM_TRACK_METRES_PER_UNIT } from "./customTrackLayout";
import { getClassicTrackRules } from "./track";
import { classicCompletedLaps } from "./classicProgress";
import { DEFAULT_RACE_CLASS, getRaceClassProfile, normalizeRaceClass, type RaceClass } from "./balance";
import { stepDrafting } from "./drafting";
import { createCourseEventState, stepCourseEvents } from "./courseEvents";
import { NEUTRAL_INPUT, type DriverInput, type ExhibitionState, type KartState, type RaceState, type TimeTrialState, type TwitchDirective } from "./types";
import { defaultKartWeightForVariant, getKartWeightProfile } from "../kartBuild";
import { applyDriverProfileAppearance, getDriverProfileForKart } from "../driverProfiles";
import { deriveRaceVariant, nextRaceVariantSeed } from "./raceVariants";
import { DEFAULT_RACE_CAPACITY, getRaceCapacity, normalizeRaceCapacity, normalizeRaceHumanSlots } from "./raceCapacity";
import { createKartState } from "./createKartState";

const FINISH_GRACE_SECONDS = 18;
const START_BOOST_WINDOW = 0.9;
const START_BOOST_THRESHOLD = 0.35;

/** Keep Hiccups' speed ceiling authoritative even while a kart has a boost. */
export function capHiccupsSpeed(kart: KartState, raceClass: RaceClass): void {
  if (kart.hiccupTime <= 0) return;
  const tuning = getRaceClassProfile(raceClass);
  const build = getKartWeightProfile(kart.weightClass);
  const cap = tuning.maxSpeed * build.maxSpeed * HICCUPS_SPEED_CAP;
  const speed = Math.hypot(kart.velocityX, kart.velocityZ);
  if (speed <= cap || speed <= 1e-6) {
    kart.speed = speed;
    return;
  }
  const ratio = cap / speed;
  kart.velocityX *= ratio;
  kart.velocityZ *= ratio;
  kart.speed = cap;
}

export function createRace(
  humanSlots = 1,
  trackId = DEFAULT_TRACK_ID,
  raceClass: RaceClass = DEFAULT_RACE_CLASS,
  variantSeed = 0,
  kartCapacity: number = DEFAULT_RACE_CAPACITY,
): RaceState {
  trackId = getTrackDefinition(trackId).id;
  raceClass = normalizeRaceClass(raceClass);
  const capacity = normalizeRaceCapacity(kartCapacity);
  const humanCount = normalizeRaceHumanSlots(humanSlots, capacity);
  const raceVariant = deriveRaceVariant(variantSeed, trackId);
  const trackLength = getTrackLength(trackId);
  const classicLayout = isClassicTrackId(trackId) ? getCustomTrackLayout(trackId) : null;
  const sourceIndexedCursor = usesSourceIndexedCursor(trackId);
  const classicGrid = classicLayout ? buildClassicStartingGrid(
    classicLayout.samples.slice(0, -1).map(sample => [sample.x / CUSTOM_TRACK_METRES_PER_UNIT, sample.z / CUSTOM_TRACK_METRES_PER_UNIT] as const),
    classicLayout.definition.width,
  ) : null;
  const karts: KartState[] = Array.from({ length: capacity }, (_, index) => {
    const classicSlot = classicGrid?.[index];
    const classicSample = classicSlot && classicLayout?.samples[classicSlot.sampleIndex];
    const distance = classicSample && classicLayout ? classicSample.distance - classicLayout.length : index === 0 ? 0 : -index * 5.4;
    // v2's right normal is the inverse of v1's planar left normal.
    const lateral = classicSlot ? -classicSlot.lateral * CUSTOM_TRACK_METRES_PER_UNIT : index % 2 === 0 ? -2.5 : 2.5;
    const transform = classicSlot && classicSample ? {
      x: classicSlot.x * CUSTOM_TRACK_METRES_PER_UNIT,
      y: classicSample.y + 0.14,
      z: classicSlot.y * CUSTOM_TRACK_METRES_PER_UNIT,
      yaw: Math.atan2(Math.cos(classicSlot.heading), Math.sin(classicSlot.heading)),
    } : createGridTransform(distance, lateral, trackId);
    const sourceSampleIndex = classicSlot?.sampleIndex
      ?? (sourceIndexedCursor ? sourceSampleIndexAtDistance(trackId, distance) : undefined);
    return createKartState({
      id: `kart-${index}`,
      index,
      controller: index < humanCount ? "human" : "ai",
      distance,
      lateral,
      pose: transform,
      sourceSampleIndex,
      ...(classicSlot ? { classicGrid: { nextCheckpoint: 0, gatesPassed: 0, progress: 0 } } : {}),
    });
  });
  return {
    trackId,
    raceVariant,
    raceClass,
    phase: "menu",
    elapsed: 0,
    countdown: 3,
    totalLaps: 3,
    trackLength,
    karts,
    itemBoxes: createItemBoxes(trackId, raceVariant),
    projectiles: [],
    hazards: createHazards(trackId, raceVariant),
    nextProjectileId: 1,
    courseEvent: createCourseEventState(trackId, raceVariant),
  };
}

export function createTimeTrialRace(trackId = DEFAULT_TRACK_ID, raceClass: RaceClass = DEFAULT_RACE_CLASS, variantSeed = 0): TimeTrialState {
  const race = createRace(1, trackId, raceClass, variantSeed);
  race.karts = race.karts.slice(0, 1);
  race.itemBoxes = [];
  race.projectiles = [];
  race.hazards = [];
  delete race.cup;
  return Object.assign(race, {
    mode: "time-trial" as const,
    timeTrial: {
      lapStartedAt: 0,
      currentLapTime: 0,
      lastLapTime: null,
      bestLapTime: null,
      completedLaps: [],
    },
  });
}

export function isTimeTrialRace(race: RaceState): race is TimeTrialState {
  return (race as Partial<TimeTrialState>).mode === "time-trial";
}

/** Create a normal full-grid race without attaching championship state. */
export function createExhibitionRace(
  trackId = DEFAULT_TRACK_ID,
  raceClass: RaceClass = DEFAULT_RACE_CLASS,
  variantSeed = 0,
  kartCapacity: number = DEFAULT_RACE_CAPACITY,
): ExhibitionState {
  const race = createRace(1, trackId, raceClass, variantSeed, kartCapacity);
  delete race.cup;
  return Object.assign(race, { mode: "exhibition" as const });
}

export function isExhibitionRace(race: RaceState): race is ExhibitionState {
  return (race as Partial<ExhibitionState>).mode === "exhibition";
}

export function beginRace(race: RaceState): void {
  if (race.karts.length === 0) {
    race.phase = "menu"; race.elapsed = 0; race.countdown = 0;
    return;
  }
  race.phase = "countdown";
  race.elapsed = 0;
  race.countdown = 3;
  race.courseEvent = createCourseEventState(race.trackId, race.raceVariant);
  for (const kart of race.karts) {
    kart.courseEventCooldown = 0; kart.courseEventHitCount = 0;
    kart.itemDodgeTime = 0; kart.itemImmunityTime = 0; kart.ghostTime = 0; kart.hiccupTime = 0; kart.spinRecoveryTime = 0;
    kart.itemHitCount = 0; kart.lastItemHit = null; kart.itemStealCount = 0; kart.lastItemStolen = null; kart.itemGuardCount = 0; kart.kegDodgeCount = 0; kart.lastItemDodge = null;
  }
  if (isTimeTrialRace(race)) {
    race.timeTrial = {
      lapStartedAt: 0,
      currentLapTime: 0,
      lastLapTime: null,
      bestLapTime: null,
      completedLaps: [],
    };
  }
}

export function resetRace(race: RaceState): void {
  if (isExhibitionRace(race)) {
    // Exhibition restarts are rematches, so keep the player's selected
    // appearance/name (and any Twitch ownership) while rebuilding the normal
    // full grid.  It must never inherit a stale championship object.
    resetRacePreservingRoster(race, race.trackId, nextRaceVariantSeed(race.raceVariant.seed));
    delete race.cup;
    return;
  }
  const capacity = getRaceCapacity(race);
  Object.assign(race, isTimeTrialRace(race)
    ? createTimeTrialRace(race.trackId, race.raceClass, race.raceVariant.seed)
    : createRace(1, race.trackId, race.raceClass, nextRaceVariantSeed(race.raceVariant.seed), capacity));
}

export function resetRacePreservingRoster(race: RaceState, nextTrackId = race.trackId, variantSeed = nextRaceVariantSeed(race.raceVariant.seed)): void {
  const capacity = getRaceCapacity(race);
  const roster = race.karts.filter((kart) => kart.controller !== "ai").map((kart) => ({
    id: kart.id,
    driverProfileId: kart.driverProfileId,
    name: kart.name,
    controller: kart.controller,
    autopilot: kart.autopilot === true,
    twitchLogin: kart.twitchLogin,
    color: kart.color,
    accentColor: kart.accentColor,
    variant: kart.variant,
    livery: kart.livery,
    wheelStyle: kart.wheelStyle,
    driverColor: kart.driverColor,
    weightClass: kart.weightClass,
    pattern: kart.pattern,
    topper: kart.topper,
    sticker: kart.sticker,
    underglow: kart.underglow,
    horn: kart.horn,
    skidColor: kart.skidColor,
    trail: kart.trail,
    raceNumber: kart.raceNumber,
  }));
  const replacement = isTimeTrialRace(race)
    ? createTimeTrialRace(nextTrackId, race.raceClass, variantSeed)
    : isExhibitionRace(race)
      ? createExhibitionRace(nextTrackId, race.raceClass, variantSeed, capacity)
      : createRace(0, nextTrackId, race.raceClass, variantSeed, capacity);
  // `createExhibitionRace` intentionally starts with one human.  Clear that
  // default before restoring the prior non-AI roster, otherwise a human that
  // occupied another slot would leave two human controllers after a reset.
  if (isExhibitionRace(race)) {
    for (const kart of replacement.karts) {
      if (kart.controller === "human") kart.controller = "ai";
    }
  }
  Object.assign(race, replacement);
  for (const assignment of roster) {
    const kart = race.karts.find((candidate) => candidate.id === assignment.id);
    if (!kart) continue;
    if (assignment.driverProfileId === undefined) delete kart.driverProfileId;
    else kart.driverProfileId = assignment.driverProfileId;
    kart.name = assignment.name;
    kart.controller = assignment.controller;
    kart.autopilot = assignment.controller === "human" && assignment.autopilot === true;
    kart.twitchLogin = assignment.twitchLogin;
    kart.color = assignment.color;
    kart.accentColor = assignment.accentColor;
    kart.variant = assignment.variant;
    kart.livery = assignment.livery;
    kart.wheelStyle = assignment.wheelStyle;
    kart.driverColor = assignment.driverColor;
    kart.weightClass = assignment.weightClass;
    kart.pattern = assignment.pattern;
    kart.topper = assignment.topper;
    kart.sticker = assignment.sticker;
    kart.underglow = assignment.underglow;
    kart.horn = assignment.horn;
    kart.skidColor = assignment.skidColor;
    kart.trail = assignment.trail;
    kart.raceNumber = assignment.raceNumber;
  }
  if (isExhibitionRace(race)) delete race.cup;
}

export interface AuthoritativeRaceRules {
  /** Server stream races wait for the entire field or this podium-relative
   * deadline. Omitted rules retain the local game's human finish presentation. */
  readonly dnfSeconds: number;
  /** Supply live connection identities so disconnected grace sessions do not
   * hold the legacy all-connected-humans finish condition open. */
  readonly connectedHumanKartIds?: ReadonlySet<string>;
}

export function stepRace(race: RaceState, inputs: DriverInput | ReadonlyMap<string, DriverInput>, dt: number, rules?: AuthoritativeRaceRules): void {
  if (rules && (!Number.isInteger(rules.dnfSeconds) || rules.dnfSeconds < 5 || rules.dnfSeconds > 120)) {
    throw new Error("Invalid authoritative race finish deadline");
  }
  if (race.karts.length === 0) {
    race.phase = "menu"; race.elapsed = 0; race.countdown = 0;
    return;
  }
  if (race.phase === "menu" || race.phase === "finished") return;
  const continuedResults = race.phase === "results";
  race.elapsed += dt;
  if (race.phase === "countdown") {
    race.countdown = Math.max(0, 3 - race.elapsed);
    for (const kart of race.karts) {
      const autonomous = kart.controller !== "human" || kart.autopilot === true;
      const input = !autonomous ? (inputs instanceof Map ? inputs.get(kart.id) ?? NEUTRAL_INPUT : inputs) : NEUTRAL_INPUT;
      if (autonomous && race.countdown <= START_BOOST_WINDOW) {
        // AI, Twitch and an autopiloted human cannot press the accelerator
        // during the countdown, so give them a consistent competent (not perfect) start.
        kart.launchCharge = Math.min(0.58, kart.launchCharge + dt * 1.2);
      } else if (input.throttle > 0.5) {
        const rate = race.countdown <= START_BOOST_WINDOW ? 1.35 : race.countdown > 1.15 ? -0.7 : 0;
        kart.launchCharge = Math.max(-1, Math.min(1, kart.launchCharge + rate * input.throttle * dt));
      } else if (race.countdown <= START_BOOST_WINDOW) {
        kart.launchCharge = Math.max(0, kart.launchCharge - dt * 0.55);
      }
    }
    if (race.elapsed + 1e-9 < 3) return;
    race.phase = "race";
    race.elapsed = 0;
    const tuning = getRaceClassProfile(race.raceClass);
    for (const kart of race.karts) {
      if (kart.launchCharge >= START_BOOST_THRESHOLD) {
        const build = getKartWeightProfile(kart.weightClass);
        const launchSpeed = tuning.maxSpeed * (0.48 + kart.launchCharge * 0.22) * build.launch;
        kart.velocityX = Math.sin(kart.yaw) * launchSpeed;
        kart.velocityZ = Math.cos(kart.yaw) * launchSpeed;
        kart.speed = launchSpeed;
        kart.boostTime = Math.max(kart.boostTime, (0.55 + kart.launchCharge * 0.65) * build.launch);
      }
      kart.launchCharge = 0;
    }
  }

  const timeTrial = isTimeTrialRace(race);
  const classic = getClassicTrackRules(race.trackId);
  if (!timeTrial) stepDrafting(race, dt);
  for (const kart of race.karts) {
    if (kart.finishedAt !== null) continue;
    const humanInput = inputs instanceof Map ? inputs.get(kart.id) : kart.controller === "human" ? inputs : undefined;
    const autonomous = kart.controller !== "human" || kart.autopilot === true;
    let input = autonomous ? getAiInput(kart, race, race.elapsed) : humanInput ?? NEUTRAL_INPUT;
    const previousDistance = kart.distance;
    const previousClassicLaps = classic ? classicCompletedLaps(kart, classic) : 0;
    if (!timeTrial) {
      stepKartItems(race, kart, input, dt);
      input = applyHiccupsInput(kart, input, race.elapsed);
      capHiccupsSpeed(kart, race.raceClass);
    }
    // Everyone now drives the same steering and drift physics. AI/Twitch can
    // still choose different lines and receive the existing bounded pace
    // model, but no longer get hidden cornering or recovery authority.
    stepKart(kart, input, dt, race.trackId, race.raceClass, kart.controller === "human" ? 0 : getAiPaceAssist(kart, race), 1);
    if (!timeTrial) capHiccupsSpeed(kart, race.raceClass);
    if (!timeTrial) stepKartHazards(race, kart, dt);
    const completedClassicLaps = classic ? classicCompletedLaps(kart, classic) : 0;
    kart.lap = classic ? Math.min(race.totalLaps, completedClassicLaps + 1) : Math.max(1, Math.floor(Math.max(0, kart.distance) / race.trackLength) + 1);
    if (timeTrial) {
      if (classic) {
        if (completedClassicLaps > previousClassicLaps) {
          const lineDistance = completedClassicLaps * race.trackLength;
          const forward = kart.distance - previousDistance;
          const fraction = forward > 0 && previousDistance <= lineDistance && kart.distance >= lineDistance
            ? Math.max(0, Math.min(1, (lineDistance - previousDistance) / forward)) : 1;
          recordTimeTrialLap(race, race.elapsed - dt + dt * fraction);
        }
        race.timeTrial.currentLapTime = race.timeTrial.completedLaps.length >= race.totalLaps ? 0 : Math.max(0, race.elapsed - race.timeTrial.lapStartedAt);
      } else stepTimeTrialTiming(race, previousDistance, kart.distance, race.elapsed - dt, race.elapsed);
      kart.lap = Math.min(race.totalLaps, race.timeTrial.completedLaps.length + 1);
      if (race.timeTrial.completedLaps.length >= race.totalLaps) kart.finishedAt = race.timeTrial.lapStartedAt;
    } else if (classic ? completedClassicLaps >= race.totalLaps : kart.distance >= race.trackLength * race.totalLaps) {
      // Preserve the crossing order inside this fixed tick. Assigning every
      // finisher race.elapsed made same-tick photo finishes fall back to the
      // stable kart-array order rather than the racer that reached the line.
      const finishDistance = race.trackLength * race.totalLaps;
      const forwardDistance = kart.distance - previousDistance;
      const fraction = forwardDistance > 0 && (!classic || previousDistance <= finishDistance && kart.distance >= finishDistance)
        ? Math.max(0, Math.min(1, (finishDistance - previousDistance) / forwardDistance))
        : 1;
      kart.finishedAt = race.elapsed - dt + dt * fraction;
    }
  }
  // Finished racers are ghosts for the remainder of the results window. They
  // must not remain physical blockers for racers still approaching the line,
  // and collision correction must not displace their final pose.
  const collisionKarts = timeTrial ? [] : resolveKartCollisions(race.karts.filter((kart) => kart.finishedAt === null), race.trackId);
  for (const kart of collisionKarts) {
    // Preserve the last checkpoint observation: post-step contact may move
    // a kart across a gate, which its next physics step must still observe.
    const projection = constrainKartToOuterBand(kart, race.trackId, false);
    if (!kart.airborne) kart.y = projection.position.y + 0.14;
  }
  if (!timeTrial) {
    // Course events are authoritative and run after movement/collisions so a
    // hit is resolved from the same final position every fixed tick for
    // human, AI, Twitch and server simulations alike.
    stepCourseEvents(race, dt);
    stepProjectiles(race, dt);
    race.hazards = race.hazards.filter((hazard) => hazard.expiresAt === undefined || hazard.expiresAt > race.elapsed);
  }

  const humanKarts = race.karts.filter((kart) => kart.controller === "human");
  const multipleHumans = humanKarts.length > 1;
  rankRaceKarts(race.karts, multipleHumans && !timeTrial);
  const player = humanKarts[0] ?? race.karts[0];
  const firstFinishedAt = race.karts.reduce<number | null>((first, kart) => kart.finishedAt === null ? first : first === null ? kart.finishedAt : Math.min(first, kart.finishedAt), null);
  if (rules && !timeTrial) {
    // A DNF stays unfinished: do not invent a crossing time or classify a
    // distant human as finished before other real entrants get their window.
    // Final ordering already puts actual finishers ahead of distance-ranked DNFs.
    const finishTimes = race.karts.flatMap(kart => kart.finishedAt === null ? [] : [kart.finishedAt]).sort((a, b) => a - b);
    const podiumAt = finishTimes[Math.min(3, race.karts.length) - 1];
    const connectedHumans = humanKarts.filter(kart => !rules.connectedHumanKartIds || rules.connectedHumanKartIds.has(kart.id));
    if (race.karts.length > 0 && (finishTimes.length === race.karts.length
      || connectedHumans.length > 0 && connectedHumans.every(kart => kart.finishedAt !== null)
      || podiumAt !== undefined && race.elapsed - podiumAt >= rules.dnfSeconds)) race.phase = "finished";
    return;
  }
  const graceElapsed = firstFinishedAt !== null && race.elapsed - firstFinishedAt >= FINISH_GRACE_SECONDS;
  let graceClassificationApplied = false;
  if (race.phase === "race" && (!multipleHumans || timeTrial) && player.finishedAt === null && graceElapsed) {
    player.finishedAt = race.elapsed;
    graceClassificationApplied = true;
  } else if (race.phase === "race" && !timeTrial && multipleHumans && humanKarts.some((kart) => kart.finishedAt === null) && graceElapsed) {
    // All unfinished human racers are classified at the same cutoff instant.
    // Their distance is the authoritative tiebreak, with id providing a stable
    // result when two controllers occupy the exact same progress point.
    for (const kart of humanKarts.filter((candidate) => candidate.finishedAt === null)) kart.finishedAt = race.elapsed;
    graceClassificationApplied = true;
  }
  if (graceClassificationApplied) {
    // The championship/server award happens immediately after this step. The
    // forced finisher(s) must therefore be ranked in this same tick, not one
    // simulation tick later.
    rankRaceKarts(race.karts, multipleHumans && !timeTrial);
  }
  const humanFinishReady = (timeTrial || !multipleHumans)
    ? player.finishedAt !== null
    : humanKarts.every((kart) => kart.finishedAt !== null);
  if (humanFinishReady && race.phase === "race") race.phase = "results";
  if (continuedResults && race.phase === "results") {
    const allFinished = race.karts.every((kart) => kart.finishedAt !== null);
    const latestHumanFinish = humanKarts.reduce<number | null>((latest, kart) => kart.finishedAt === null ? latest : latest === null ? kart.finishedAt : Math.max(latest, kart.finishedAt), null);
    const resultsTailAt = !timeTrial && multipleHumans ? latestHumanFinish : player.finishedAt;
    if (allFinished || race.elapsed - (resultsTailAt ?? race.elapsed) >= 12) race.phase = "finished";
  }
}

function rankRaceKarts(karts: readonly KartState[], distanceOnFinishTie = false): void {
  [...karts].sort((a, b) => {
    if (a.finishedAt !== null && b.finishedAt !== null) {
      const finishOrder = a.finishedAt - b.finishedAt;
      if (finishOrder !== 0 || !distanceOnFinishTie) return finishOrder;
      const distanceOrder = (b.classicProgress ?? b.distance) - (a.classicProgress ?? a.distance);
      return distanceOrder !== 0 ? distanceOrder : a.id.localeCompare(b.id);
    }
    if (a.finishedAt !== null) return -1;
    if (b.finishedAt !== null) return 1;
    return (b.classicProgress ?? b.distance) - (a.classicProgress ?? a.distance);
  }).forEach((kart, index) => { kart.place = index + 1; });
}

/**
 * Controller ownership changes must not carry a previous driver's countdown
 * launch timing into the new driver's start. Keep every other live kart field
 * intact so a transfer during a race remains a controller handoff, not a
 * hidden reset.
 */
function transferKartController(kart: KartState, controller: KartState["controller"]): void {
  if (kart.controller === controller) return;
  kart.controller = controller;
  if (controller !== "human") kart.autopilot = false;
  kart.launchCharge = 0;
}

/** Records every forward start-line crossing in a tick at its interpolated time. */
function recordTimeTrialLap(race: TimeTrialState, crossedAt: number): void {
  const timing = race.timeTrial;
  if (timing.completedLaps.length >= race.totalLaps) return;
  const lapTime = Math.max(0, crossedAt - timing.lapStartedAt);
  timing.completedLaps.push(lapTime);
  timing.lastLapTime = lapTime;
  timing.bestLapTime = timing.bestLapTime === null ? lapTime : Math.min(timing.bestLapTime, lapTime);
  timing.lapStartedAt = crossedAt;
}

export function stepTimeTrialTiming(
  race: TimeTrialState,
  previousDistance: number,
  currentDistance: number,
  tickStartedAt: number,
  tickEndedAt: number,
): void {
  const timing = race.timeTrial;
  if (race.phase !== "race" || timing.completedLaps.length >= race.totalLaps) return;
  const tickDuration = Math.max(0, tickEndedAt - tickStartedAt);
  const forwardDistance = currentDistance - previousDistance;
  while (timing.completedLaps.length < race.totalLaps) {
    const finishDistance = race.trackLength * (timing.completedLaps.length + 1);
    if (previousDistance >= finishDistance || currentDistance < finishDistance || forwardDistance <= 0) break;
    const fraction = Math.max(0, Math.min(1, (finishDistance - previousDistance) / forwardDistance));
    const crossedAt = tickStartedAt + tickDuration * fraction;
    recordTimeTrialLap(race, crossedAt);
  }
  timing.currentLapTime = timing.completedLaps.length >= race.totalLaps
    ? 0
    : Math.max(0, tickEndedAt - timing.lapStartedAt);
}

export function claimHumanKart(race: RaceState, name: string): KartState | null {
  const duelRivalKartId = race.cup?.duelRivalKartId ?? null;
  const kart = race.karts.find((candidate) => candidate.controller === "ai" && candidate.id !== duelRivalKartId);
  if (!kart) return null;
  transferKartController(kart, "human");
  kart.name = name;
  return kart;
}

export function releaseHumanKart(race: RaceState, kartId: string): void {
  const kart = race.karts.find((candidate) => candidate.id === kartId && candidate.controller === "human");
  if (!kart) return;
  transferKartController(kart, "ai");
  kart.twitchLogin = null;
  kart.twitchDirective = "steady";
  kart.directiveUntil = 0;
  kart.laneBias = 0;
  kart.laneBiasUntil = 0;
  kart.pendingItemUse = false;
  const profile = getDriverProfileForKart(kart);
  if (profile) {
    applyDriverProfileAppearance(kart, profile);
    return;
  }
  const index = Number(kart.id.replace(/\D/g, ""));
  kart.name = index === 0 ? "YOU" : `RACER ${index + 1}`;
  kart.color = 0xf3c552;
  kart.accentColor = 0xf3c552;
  kart.variant = index % 7;
  kart.livery = 0;
  kart.wheelStyle = index % 5;
  kart.driverColor = kart.color;
  kart.weightClass = defaultKartWeightForVariant(kart.variant);
  kart.pattern = "solid";
  kart.topper = "none";
  kart.sticker = "none";
  kart.underglow = 0;
  kart.horn = "toot";
  kart.skidColor = 0x120c08;
  kart.trail = "bubbles";
  kart.raceNumber = "";
}

export function joinTwitchKart(race: RaceState, login: string, displayName: string): KartState | null {
  const existing = race.karts.find((kart) => kart.twitchLogin === login);
  if (existing) return existing;
  const duelRivalKartId = race.cup?.duelRivalKartId ?? null;
  const kart = race.karts.find((candidate) => candidate.controller === "ai" && candidate.id !== "kart-0" && candidate.id !== duelRivalKartId);
  if (!kart) return null;
  transferKartController(kart, "twitch");
  kart.twitchLogin = login;
  kart.name = displayName;
  return kart;
}

export function setTwitchDirective(race: RaceState, login: string, directive: TwitchDirective, duration = 8): boolean {
  if (race.phase !== "race") return false;
  const kart = race.karts.find((candidate) => candidate.twitchLogin === login);
  if (!kart) return false;
  kart.twitchDirective = directive;
  kart.directiveUntil = race.elapsed + Math.max(1, Math.min(20, duration));
  return true;
}

export function queueTwitchItemUse(race: RaceState, login: string): KartState["heldItem"] | null {
  const kart = race.karts.find((candidate) => candidate.twitchLogin === login);
  if (!kart || race.phase !== "race" || !kart.heldItem) return null;
  kart.pendingItemUse = true;
  return kart.heldItem;
}

export function setTwitchLane(race: RaceState, login: string, direction: -1 | 1, duration = 6): boolean {
  const kart = race.karts.find((candidate) => candidate.twitchLogin === login);
  if (!kart || race.phase !== "race") return false;
  kart.laneBias = direction * 6;
  kart.laneBiasUntil = race.elapsed + Math.max(1, Math.min(10, duration));
  return true;
}

export function honkTwitchKart(race: RaceState, login: string): boolean {
  const kart = race.karts.find((candidate) => candidate.twitchLogin === login);
  if (!kart) return false;
  kart.hornSeq += 1;
  return true;
}
